import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { BehaviorSubject, Observable, of } from "rxjs";
import { map } from "rxjs/operators";

import { SearchService } from "./search.service";
import { SearchItem, SearchObjectType } from "../domains/searchItem";
import { CommandSection } from "../shared/ds/eva-command-palette/eva-command-palette.component";
import { EvaCompanyService } from "../features/eva-company/services/evaCompany.service";

// =============================================================================
// CATEGORY CONFIG
// =============================================================================

export interface CategoryConfig {
  label: string;
  icon: string;
}

export const CATEGORY_CONFIG: Record<string, CategoryConfig> = {
  CUSTOMER: { label: "Clients", icon: "heroUsers" },
  OFFER: { label: "Offres", icon: "heroPencilSquare" },
  PROJECT: { label: "Chantiers", icon: "heroBriefcase" },
  INVOICE_ISSUED: { label: "Factures émises", icon: "heroDocument" },
  INVOICE_RECEIVED: { label: "Factures reçues", icon: "heroDocumentArrowDown" },
  SUPPLIER: { label: "Fournisseurs", icon: "heroTruck" },
  EMPLOYEE: { label: "Collaborateurs", icon: "heroIdentification" },
  WORKSHOP: { label: "Ateliers", icon: "heroWrench" },
  CONTACT: { label: "Contacts", icon: "heroIdentification" },
  INTERVENTION: { label: "Interventions", icon: "heroWrench" },
  CONTRACT: { label: "Contrats", icon: "heroDocumentCheck" },
  PURCHASE_ORDER: { label: "Commandes", icon: "heroShoppingCart" },
  INTERNAL_ARTICLE: { label: "Articles", icon: "heroCube" },
};

// =============================================================================
// NAVIGATION ITEMS
// =============================================================================

interface NavigationItem {
  label: string;
  route: string;
  icon: string;
  synonyms: string[];
}

const CATEGORY_ORDER: SearchObjectType[] = [
  SearchObjectType.PROJECT,
  SearchObjectType.OFFER,
  SearchObjectType.CUSTOMER,
  SearchObjectType.SUPPLIER,
  SearchObjectType.CONTACT,
  SearchObjectType.INVOICE_ISSUED,
  SearchObjectType.INVOICE_RECEIVED,
  SearchObjectType.CONTRACT,
  SearchObjectType.EMPLOYEE,
  SearchObjectType.INTERVENTION,
  SearchObjectType.PURCHASE_ORDER,
  SearchObjectType.WORKSHOP,
  SearchObjectType.INTERNAL_ARTICLE,
];

const NAVIGATION_ITEMS: NavigationItem[] = [
  { label: "Tableau de bord", route: "/home", icon: "heroHome", synonyms: ["dashboard", "accueil"] },
  { label: "Chantiers", route: "/projects", icon: "heroBriefcase", synonyms: ["projets", "construction", "ouvrage"] },
  { label: "Planning", route: "/admin/team-planning", icon: "heroCalendar", synonyms: ["calendrier", "agenda"] },
  { label: "Offres", route: "/offers", icon: "heroPencilSquare", synonyms: ["offre", "soumissions", "estimations"] },
  {
    label: "Factures clients",
    route: "/invoices/issued",
    icon: "heroDocument",
    synonyms: ["factures émises", "factures emises", "décomptes", "decomptes"],
  },
  {
    label: "Clients & Prospects",
    route: "/customers",
    icon: "heroUsers",
    synonyms: ["clients", "prospects", "maitre d'ouvrage"],
  },
  { label: "Catalogues", route: "/catalog", icon: "heroBookOpen", synonyms: ["articles", "produits", "prestations"] },
  {
    label: "Bons de commande",
    route: "/purchase-orders",
    icon: "heroShoppingCart",
    synonyms: ["commandes fournisseur"],
  },
  {
    label: "Factures d'achat",
    route: "/invoices/received",
    icon: "heroDocumentArrowDown",
    synonyms: ["factures reçues", "factures recues", "factures fournisseur"],
  },
  {
    label: "Fournisseurs",
    route: "/suppliers",
    icon: "heroTruck",
    synonyms: ["fournisseurs", "sous-traitants"],
  },
  {
    label: "Paramètres",
    route: "/settings",
    icon: "heroCog6Tooth",
    synonyms: ["réglages", "reglages", "configuration"],
  },
  {
    label: "Collaborateurs",
    route: "/employees",
    icon: "heroIdentification",
    synonyms: ["employés", "employes", "équipe", "equipe", "personnel"],
  },
  { label: "Contacts", route: "/contacts", icon: "heroIdentification", synonyms: ["carnet d'adresses"] },
  {
    label: "Interventions",
    route: "/interventions",
    icon: "heroWrench",
    synonyms: ["dépannages", "depannages", "entretiens", "sav"],
  },
  { label: "Banking", route: "/admin/banking", icon: "heroBanknotes", synonyms: ["banque", "paiements"] },
  { label: "Comptabilité", route: "/accounting", icon: "heroCalculator", synonyms: ["comptable", "bilan"] },
];

// =============================================================================
// QUICK ACTIONS
// =============================================================================

interface QuickAction {
  label: string;
  route: string;
  icon: string;
  shortcut: string;
}

const QUICK_ACTIONS: QuickAction[] = [
  { label: "Créer une offre", route: "/offers/create", icon: "heroPencilSquare", shortcut: "N O" },
  { label: "Ajouter un client", route: "/customers/create", icon: "heroUserPlus", shortcut: "N C" },
  { label: "Créer une facture", route: "/invoices/issued/create", icon: "heroDocument", shortcut: "N F" },
  { label: "Créer un chantier", route: "/projects/create", icon: "heroBriefcase", shortcut: "N P" },
  { label: "Créer une intervention", route: "/interventions/create", icon: "heroWrench", shortcut: "N I" },
  { label: "Ajouter un fournisseur", route: "/suppliers/create", icon: "heroTruck", shortcut: "N S" },
];

// =============================================================================
// SLASH COMMANDS
// =============================================================================

interface SlashCommand {
  prefix: string;
  synonyms: string[];
  filterTypes?: SearchObjectType[];
  mode?: "navigation" | "actions";
}

const SLASH_COMMANDS: SlashCommand[] = [
  { prefix: "/clients", synonyms: ["/prospects"], filterTypes: [SearchObjectType.CUSTOMER] },
  { prefix: "/offres", synonyms: ["/offre", "/soumissions"], filterTypes: [SearchObjectType.OFFER] },
  {
    prefix: "/factures",
    synonyms: [],
    filterTypes: [SearchObjectType.INVOICE_ISSUED, SearchObjectType.INVOICE_RECEIVED],
  },
  { prefix: "/projets", synonyms: ["/chantiers"], filterTypes: [SearchObjectType.PROJECT] },
  { prefix: "/interventions", synonyms: ["/depannages"], filterTypes: [SearchObjectType.INTERVENTION] },
  { prefix: "/fournisseurs", synonyms: [], filterTypes: [SearchObjectType.SUPPLIER] },
  { prefix: "/employes", synonyms: [], filterTypes: [SearchObjectType.EMPLOYEE] },
  { prefix: "/aller", synonyms: ["/nav"], mode: "navigation" },
  { prefix: "/creer", synonyms: ["/new"], mode: "actions" },
];

// =============================================================================
// PARSED QUERY
// =============================================================================

export interface ParsedQuery {
  /** Raw query without the slash prefix */
  query: string;
  /** Matched slash command (null if not a slash command) */
  slashCommand: SlashCommand | null;
  /** Whether this is a slash command */
  isSlashCommand: boolean;
}

// =============================================================================
// SERVICE
// =============================================================================

@Injectable({
  providedIn: "root",
})
export class CommandPaletteService {
  // ==========================================================================
  // State
  // ==========================================================================

  private isOpenSubject = new BehaviorSubject<boolean>(false);
  public isOpen$ = this.isOpenSubject.asObservable();

  private readonly HISTORY_MAX = 5;

  constructor(
    private searchService: SearchService,
    private evaCompanyService: EvaCompanyService,
    private router: Router,
  ) {}

  // ==========================================================================
  // Open / Close / Toggle
  // ==========================================================================

  open(): void {
    this.isOpenSubject.next(true);
  }

  close(): void {
    this.isOpenSubject.next(false);
  }

  toggle(): void {
    this.isOpenSubject.next(!this.isOpenSubject.getValue());
  }

  // ==========================================================================
  // Search
  // ==========================================================================

  /**
   * Search backend and convert SearchItem[] to CommandSection[] grouped by object_category.
   */
  search(query: string, filterTypes?: SearchObjectType[]): Observable<CommandSection[]> {
    const sanitizedQuery = this.sanitizeQuery(query);
    if (!sanitizedQuery || sanitizedQuery.length < 2) {
      return of([]);
    }

    return this.searchService.getSearch(sanitizedQuery, 30).pipe(
      map((items: SearchItem[]) => {
        // Filter by type if specified
        let filtered = items;
        if (filterTypes && filterTypes.length > 0) {
          filtered = items.filter((item) => filterTypes.includes(item.object_category));
        }

        return this.groupSearchItemsToSections(filtered);
      }),
    );
  }

  sanitizeQuery(query: string): string {
    return this.searchService.sanitizeQuery(query);
  }

  // ==========================================================================
  // Navigation Section
  // ==========================================================================

  getNavigationSection(filterQuery?: string): CommandSection {
    let items = NAVIGATION_ITEMS;

    if (filterQuery && filterQuery.trim()) {
      const q = this.normalizeText(filterQuery);
      items = items.filter(
        (item) =>
          this.normalizeText(item.label).includes(q) || item.synonyms.some((s) => this.normalizeText(s).includes(q)),
      );
    }

    return {
      id: "navigation",
      label: "Navigation",
      icon: "evaCompass",
      items: items.map((nav) => ({
        id: `nav-${nav.route}`,
        label: nav.label,
        icon: nav.icon,
        action: () => this.router.navigate([nav.route]),
      })),
    };
  }

  // ==========================================================================
  // Actions Section
  // ==========================================================================

  getActionsSection(filterQuery?: string): CommandSection {
    let actions = QUICK_ACTIONS;

    if (filterQuery && filterQuery.trim()) {
      const q = this.normalizeText(filterQuery);
      actions = actions.filter((a) => this.normalizeText(a.label).includes(q));
    }

    return {
      id: "actions",
      label: "Actions rapides",
      icon: "heroBolt",
      items: actions.map((action) => ({
        id: `action-${action.route}`,
        label: action.label,
        icon: action.icon,
        shortcut: action.shortcut,
        action: () => this.router.navigate([action.route]),
      })),
    };
  }

  // ==========================================================================
  // Parse Query (slash commands)
  // ==========================================================================

  parseQuery(raw: string): ParsedQuery {
    if (!raw || !raw.startsWith("/")) {
      return { query: raw || "", slashCommand: null, isSlashCommand: false };
    }

    const parts = raw.split(" ");
    const prefix = parts[0].toLowerCase();
    const remainingQuery = parts.slice(1).join(" ");

    for (const cmd of SLASH_COMMANDS) {
      if (cmd.prefix === prefix || cmd.synonyms.includes(prefix)) {
        return { query: remainingQuery, slashCommand: cmd, isSlashCommand: true };
      }
    }

    // Unknown slash command
    return { query: raw, slashCommand: null, isSlashCommand: false };
  }

  // ==========================================================================
  // Search History
  // ==========================================================================

  private getHistoryKey(): string {
    const companyId = this.evaCompanyService.currentEvaCompany?.eva_company_id || "default";
    return `eva_cp_history_${companyId}`;
  }

  getSearchHistory(): string[] {
    try {
      const stored = localStorage.getItem(this.getHistoryKey());
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  addToSearchHistory(query: string): void {
    if (!query || query.trim().length < 2) return;

    const history = this.getSearchHistory();
    const filtered = history.filter((h) => h !== query.trim());
    const updated = [query.trim(), ...filtered].slice(0, this.HISTORY_MAX);

    try {
      localStorage.setItem(this.getHistoryKey(), JSON.stringify(updated));
    } catch {
      // Ignore localStorage errors
    }
  }

  clearSearchHistory(): void {
    try {
      localStorage.removeItem(this.getHistoryKey());
    } catch {
      // Ignore
    }
  }

  getHistorySection(): CommandSection | null {
    const history = this.getSearchHistory();
    if (history.length === 0) return null;

    return {
      id: "history",
      label: "Recherches recentes",
      icon: "evaHistory",
      items: history.map((query, index) => ({
        id: `history-${index}`,
        label: query,
        icon: "heroMagnifyingGlass",
        action: () => {
          // Will be handled by the layout via onSelect
        },
        data: { type: "history", query },
      })),
    };
  }

  // ==========================================================================
  // Private Helpers
  // ==========================================================================

  private groupSearchItemsToSections(items: SearchItem[]): CommandSection[] {
    const grouped = new Map<string, SearchItem[]>();

    for (const item of items) {
      const category = item.object_category || "OTHER";
      if (!grouped.has(category)) {
        grouped.set(category, []);
      }
      grouped.get(category)!.push(item);
    }

    const sections: CommandSection[] = [];

    grouped.forEach((categoryItems, category) => {
      const config = CATEGORY_CONFIG[category] || { label: category, icon: "evaCircle" };

      sections.push({
        id: `search-${category}`,
        label: config.label,
        icon: config.icon,
        items: categoryItems.map((item) => ({
          id: `search-${item.object_id}`,
          label: item.object_label,
          icon: config.icon,
          action: () => {
            if (item.object_link) {
              this.router.navigate([item.object_link]);
            }
          },
          data: { searchItem: item },
        })),
      });
    });

    return sections.sort((left, right) => {
      const leftIndex = CATEGORY_ORDER.indexOf(left.items[0]?.data?.searchItem?.object_category);
      const rightIndex = CATEGORY_ORDER.indexOf(right.items[0]?.data?.searchItem?.object_category);

      const normalizedLeftIndex = leftIndex === -1 ? Number.MAX_SAFE_INTEGER : leftIndex;
      const normalizedRightIndex = rightIndex === -1 ? Number.MAX_SAFE_INTEGER : rightIndex;

      return normalizedLeftIndex - normalizedRightIndex;
    });
  }

  private normalizeText(value: string): string {
    return (value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();
  }
}
